import {ActionList} from '@primer/react'
import {useVirtualizer, type VirtualItem} from '@github-ui/virtualizer'
import React, {useCallback, useRef} from 'react'

export interface FixedSizeVirtualizedListProps<T> {
  items: T[]
  itemHeight: number
  renderItem: (item: T, index: number) => React.ReactNode
  makeKey: (item: T) => string
  style?: React.CSSProperties // style to apply to the list container
  ariaControls?: string
  shouldUseActionList?: boolean // determines if ActionList or ul is used to wrap list items
  className?: string
  containerRole?: string
}

export function FixedSizeVirtualList<T>({
  items,
  itemHeight,
  style,
  renderItem,
  makeKey,
  ariaControls,
  shouldUseActionList = false,
  className,
  containerRole = 'none',
}: FixedSizeVirtualizedListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null)

  const {
    items: rows,
    totalSize,
    measureElement,
  } = useVirtualizer<HTMLElement, HTMLLIElement>({
    count: items.length,
    getScrollElement: useCallback(() => parentRef.current, []),
    estimateSize: useCallback(() => itemHeight, [itemHeight]),
    overscan: 10,
    getItemKey: index => {
      const item = items[index]
      return item === undefined ? index : makeKey(item)
    },
  })

  return (
    <ListContainer
      ref={parentRef}
      style={style}
      totalSize={totalSize}
      id={ariaControls}
      shouldUseActionList={shouldUseActionList}
      className={className}
    >
      {rows.map(row => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const item = items[row.index]!
        return (
          <ItemContainer containerRole={containerRole} key={makeKey(item)} row={row} measureElement={measureElement}>
            {renderItem(item, row.index)}
          </ItemContainer>
        )
      })}
    </ListContainer>
  )
}

function ListContainer({
  ref: forwardedRef,
  children,
  style,
  totalSize,
  id,
  shouldUseActionList,
  className,
}: React.PropsWithChildren<{
  totalSize: number
  style?: React.CSSProperties
  id?: string
  shouldUseActionList?: boolean
  className?: string
  ref?: React.Ref<HTMLDivElement>
}>) {
  return (
    <div ref={forwardedRef} style={style} className={className}>
      {shouldUseActionList ? (
        <ActionList
          id={id}
          role="menu"
          style={{height: totalSize, width: '100%', position: 'relative'}}
          selectionVariant={'single'}
        >
          {children}
        </ActionList>
      ) : (
        <ul role="menu" style={{height: totalSize, width: '100%', position: 'relative'}} id={id}>
          {children}
        </ul>
      )}
    </div>
  )
}

function ItemContainer({
  children,
  containerRole = 'none',
  row,
  measureElement,
}: React.PropsWithChildren<{
  containerRole?: string
  row: VirtualItem
  measureElement: (element: HTMLLIElement | null) => void
}>) {
  // Each row is absolutely positioned and moved with a CSS transform; these styles are all
  // required or the list virtualization will not position rows correctly.
  return (
    <li
      data-index={row.index}
      ref={measureElement}
      role={containerRole}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        transform: `translateY(${row.start}px)`,
      }}
    >
      {children}
    </li>
  )
}
